import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@/app/layout/service/layout.service';
import { Ripple } from 'primeng/ripple';
import { InputText } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadge } from 'primeng/overlaybadge';
import { AuthService } from '@/app/auth/auth.service';
import { RoleSwitcherComponent } from '@/app/layout/role-switcher.component';

@Component({
    selector: '[app-topbar]',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterModule, CommonModule, StyleClassModule, FormsModule, Ripple, InputText, ButtonModule, BadgeModule, OverlayBadge, RoleSwitcherComponent],
    template: `
        <div class="layout-topbar-start">
            <a class="layout-topbar-logo" routerLink="/">
                <img src="/images/cyan-logo-icon.png" alt="Cyan icon" class="app-brand-icon" style="height: 42px; width: auto" />
                <img src="/images/cyan-logo-text.png" alt="Cyan Healthcare Group" class="app-brand-text" style="height: 20px; width: auto" />
                <span class="app-product-name">Blue</span>
            </a>
            <a #menuButton class="layout-menu-button" (click)="onMenuButtonClick()">
                <i class="pi pi-chevron-right"></i>
            </a>

            <button class="app-config-button app-config-mobile-button" (click)="toggleConfigSidebar()">
                <i class="pi pi-cog"></i>
            </button>

            <a #mobileMenuButton class="layout-topbar-mobile-button" (click)="onTopbarMenuToggle()">
                <i class="pi pi-ellipsis-v"></i>
            </a>
        </div>

        <div class="layout-topbar-end">
            <div class="layout-topbar-actions-start">
                <span class="topbar-nav-placeholder text-muted-color text-sm"></span>
            </div>
            <div class="layout-topbar-actions-end">
                <ul class="layout-topbar-items">
                    <li class="layout-topbar-search">
                        <a pStyleClass="@next" enterFromClass="!hidden" enterActiveClass="animate-scalein" leaveToClass="!hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true" [hideOnEscape]="true" (click)="focusSearchInput()">
                            <i class="pi pi-search"></i>
                        </a>

                        <div class="layout-search-panel !hidden p-input-filled">
                            <i class="pi pi-search"></i>
                            <input #searchInput type="text" pInputText placeholder="Search" />
                            <button pButton pRipple type="button" icon="pi pi-times" rounded text pStyleClass=".layout-search-panel" leaveToClass="!hidden" leaveActiveClass="animate-fadeout"></button>
                        </div>
                    </li>
                    <li>
                        <button class="app-config-button" (click)="toggleConfigSidebar()">
                            <i class="pi pi-cog"></i>
                        </button>
                    </li>
                    <li>
                        <a pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true">
                            <p-overlay-badge severity="warn">
                                <i class="pi pi-bell align-middle!"></i>
                            </p-overlay-badge>
                        </a>
                        <div class="hidden">
                            <ul class="list-none p-0 m-0">
                                <li class="px-4 py-1">
                                    <span>You have <b>4</b> new notifications</span>
                                </li>
                            </ul>
                        </div>
                    </li>
                    <li>
                        <a pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true">
                            <i class="pi pi-table"></i>
                        </a>
                        <div class="hidden">
                            <div class="flex flex-wrap">
                                <div class="w-4/12 flex flex-col items-center p-4">
                                    <button pButton pRipple rounded class="mb-2" icon="pi pi-image dark:text-black"></button>
                                    <span>Products</span>
                                </div>
                            </div>
                        </div>
                    </li>
                    <li class="layout-topbar-role">
                        <app-role-switcher />
                    </li>
                    <li>
                        <a pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true">
                            <span class="topbar-profile-avatar" aria-hidden="true">{{ userInitials() }}</span>
                        </a>
                        <div class="hidden">
                            <ul class="list-none p-0 m-0">
                                <li class="px-4 py-2 text-sm text-muted-color">
                                    {{ currentUserName() }}
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        class="cursor-pointer flex items-center hover:bg-emphasis duration-150 transition-all px-4 py-2 w-full border-none bg-transparent text-inherit"
                                        pRipple
                                        (click)="logout()"
                                    >
                                        <i class="pi pi-power-off mr-2"></i>
                                        <span>Logout</span>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    `,
    host: {
        class: 'layout-topbar'
    },
    styles: `
        /* Quieten the sidebar-collapse button — it was the highest-contrast element on the
           page (bright yellow from the stock indigo topbar theme). Overridden scoped to
           this component only; the shared theme variable itself is untouched. */
        :host {
            --topbar-menu-button-bg: var(--cb-navy-50);
            --topbar-menu-button-hover-bg: var(--cb-navy-100);
            --topbar-menu-button-text-color: var(--cb-navy-600);
        }

        :host ::ng-deep .p-overlaybadge .p-badge {
            outline-width: 0px;
        }

        :host .layout-topbar-logo {
            display: inline-flex;
            align-items: center;
            gap: 0.75rem;
        }

        :host .app-brand-icon {
            transform: translateY(-4px);
        }

        :host .app-brand-text {
            transform: translateY(0px);
        }

        :host .app-product-name {
            font-size: 1.5rem;
            font-weight: 600;
            letter-spacing: 0.08em;
            line-height: 1;
            color: var(--topbar-item-text-color);
            user-select: none;
        }

        :host .topbar-nav-placeholder {
            display: inline-flex;
            align-items: center;
            height: 2.5rem;
        }

        /* The acting-role pill sits inline with the icon actions; center it and give it a
           little breathing room from the avatar to its right. */
        :host .layout-topbar-role {
            display: inline-flex;
            align-items: center;
            margin-right: 0.25rem;
        }

        :host .topbar-profile-avatar {
            width: 2rem;
            height: 2rem;
            min-width: 2rem;
            min-height: 2rem;
            aspect-ratio: 1;
            border-radius: 50%;
            background: #0891b2;
            color: #ffffff;
            font-size: 11px;
            font-weight: 700;
            line-height: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            letter-spacing: 0.02em;
            overflow: hidden;
            box-sizing: border-box;
        }
    `
})
export class AppTopbar {
    layoutService = inject(LayoutService);
    private readonly router = inject(Router);
    private readonly auth = inject(AuthService);

    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
    @ViewChild('menuButton') menuButton!: ElementRef<HTMLButtonElement>;
    @ViewChild('mobileMenuButton') mobileMenuButton!: ElementRef<HTMLButtonElement>;

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    toggleConfigSidebar() {
        if (this.layoutService.isSidebarActive()) {
            this.layoutService.layoutState.update((prev) => ({
                ...prev,
                overlayMenuActive: false,
                staticMenuMobileActive: false,
                menuHoverActive: false,
                configSidebarVisible: true
            }));
        } else {
            this.layoutService.toggleConfigSidebar();
        }
    }

    focusSearchInput() {
        setTimeout(() => {
            this.searchInput.nativeElement.focus();
        }, 150);
    }

    onTopbarMenuToggle() {
        this.layoutService.layoutState.update((val) => ({ ...val, topbarMenuActive: !val.topbarMenuActive }));
    }

    currentUserName(): string {
        return this.auth.currentUser()?.fullName ?? 'Signed in user';
    }

    userInitials(): string {
        const name = this.currentUserName();
        return name
            .split(' ')
            .filter(Boolean)
            .map((part) => part[0]?.toUpperCase() ?? '')
            .join('')
            .slice(0, 2);
    }

    logout(): void {
        this.auth.logout().subscribe({
            next: () => void this.router.navigate(['/login']),
            error: () => void this.router.navigate(['/login'])
        });
    }
}
